import { CircleHelp } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

import { useHelp } from "@/components/help";

import { useOnboarding } from "./OnboardingProvider";

/**
 * The header's help menu: browse the help articles, take the tour again, and bring back the getting-started checklist
 * once it was hidden. A menu button: opens onto its first item, arrow keys move, Escape or a click elsewhere closes it
 * and returns focus.
 */
export function HelpMenu() {
  const onboarding = useOnboarding();
  const help = useHelp();
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onMouseDown = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  if (!onboarding && !help) return null;

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) trigger.current?.focus();
  };
  const items: { label: string; run: () => void; returnFocus?: boolean }[] = [
    // Focus goes back to the button first, so the help panel returns it there when it closes.
    ...(help ? [{ label: "Browse help articles", run: () => help.open(), returnFocus: true }] : []),
    ...(onboarding ? [{ label: "Take the tour", run: onboarding.startTour }] : []),
    ...(onboarding?.applicable && onboarding.dismissed
      ? [{ label: "Show the getting started checklist", run: onboarding.restore }]
      : []),
  ];

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const entries = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]')];
    const at = entries.indexOf(document.activeElement as HTMLElement);
    const go = (to: number) => {
      event.preventDefault();
      entries[(to + entries.length) % entries.length]?.focus();
    };
    switch (event.key) {
      case "ArrowDown":
        return go(at + 1);
      case "ArrowUp":
        return go(at - 1);
      case "Home":
        return go(0);
      case "End":
        return go(entries.length - 1);
      case "Escape":
        event.preventDefault();
        return close(true);
      case "Tab":
        return close(false);
    }
  };

  return (
    <div ref={wrapper} data-tour="help-menu" className="relative print:hidden">
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label="Help"
        className="flex size-[38px] items-center justify-center rounded-[10px] border border-border bg-surface text-fg-muted hover:text-fg"
      >
        <CircleHelp className="size-[18px]" aria-hidden />
      </button>
      {open && (
        <div
          ref={menu}
          id={menuId}
          role="menu"
          tabIndex={-1}
          aria-label="Help"
          onKeyDown={onMenuKeyDown}
          className="absolute right-0 z-30 mt-2 w-64 overflow-hidden rounded-xl border border-border bg-surface py-1 text-sm shadow-lg"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              tabIndex={-1}
              onClick={() => {
                close(item.returnFocus === true);
                item.run();
              }}
              className="block w-full px-3 py-2 text-left text-fg hover:bg-muted focus:bg-muted focus:outline-none"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
